import { useState } from "react";
import "./SupportTicketPopup.css";

// ── Category options (matches the server-side enum) ──────────────────────────
const CATEGORIES = [
  { value: "game_crash", label: "Game Crash" },
  { value: "controller_not_responding", label: "Controller Not Responding" },
  { value: "video_audio_lag", label: "Video / Audio Lag" },
  // { value: "payment", label: "Payment Issue" },
  { value: "other", label: "Other" },
];

// ── Resolve the ticket API URL ────────────────────────────────────────────
// In-game AgriSim support ticket endpoints are served by the central backend API on port 5000.
const TICKET_API =
  process.env.REACT_APP_TICKET_SERVICE_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  "http://localhost:5000";



// ── Strip ?support and ?token from the browser URL without a page reload ─────
function clearSupportParams() {
  const url = new URL(window.location.href);
  url.searchParams.delete("support");
  url.searchParams.delete("token");
  window.history.replaceState({}, "", url.toString());
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function SupportTicketPopup({ token, streamId, onClose }) {
  const [streamUsername, setStreamUsername] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [phase, setPhase] = useState("form"); // form | submitting | success | expired
  const [showConfirmDiscard, setShowConfirmDiscard] = useState(false);

  // Form is considered dirty if any user-editable field has non-empty content
  const isDirty = Boolean(
    streamUsername.trim() ||
    email.trim() ||
    category ||
    description.trim()
  );

  const validate = () => {
    const e = {};
    const cleanUsername = streamUsername.trim();
    if (!cleanUsername) {
      e.streamUsername = "Stream Username is required";
    } else if (cleanUsername.length < 2 || cleanUsername.length > 50) {
      e.streamUsername = "Stream Username must be between 2 and 50 characters";
    } else if (!/^[a-zA-Z0-9_.\- ]{2,50}$/.test(cleanUsername)) {
      e.streamUsername = "Allowed characters: letters, numbers, spaces, _, ., -";
    }

    if (!category) {
      e.category = "Please select a category";
    }

    const cleanDesc = description.trim();
    if (!cleanDesc) {
      e.description = "Description is required";
    } else if (cleanDesc.length < 10) {
      e.description = "Description must be at least 10 characters long";
    }

    const cleanEmail = email.trim();
    if (!cleanEmail) {
      e.email = "Email is required";
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(cleanEmail)) {
      e.email = "Please enter a valid email address (e.g. name@domain.com)";
    }
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    setPhase("submitting");

    try {
      const res = await fetch(`${TICKET_API}/api/tickets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          streamUsername: streamUsername.trim(),
          category,
          description: description.trim(),
          email: email.trim(),
        }),
      });

      const data = await res.json();

      if (res.status === 403) {
        setPhase("expired");
        clearSupportParams();
        return;
      }
      if (!res.ok) throw new Error(data.message || "Submission failed");

      setPhase({ type: "success", ticketNumber: data.ticketNumber });
      clearSupportParams();
    } catch (err) {
      setPhase("form");
      setErrors({ submit: err.message || "Something went wrong. Please try again." });
    }
  };

  const handleClose = () => {
    clearSupportParams();
    onClose();
  };

  const handleAttemptClose = () => {
    // Immediate close for expired / success states (no unsaved user data)
    if (phase === "expired" || phase?.type === "success") {
      handleClose();
      return;
    }
    // If confirm screen is open, returning cancels confirm
    if (showConfirmDiscard) {
      setShowConfirmDiscard(false);
      return;
    }
    // If user has typed anything into editable fields, prompt for confirmation
    if (isDirty) {
      setShowConfirmDiscard(true);
    } else {
      handleClose();
    }
  };

  return (
    <div className="stp-backdrop" onClick={(e) => e.target.classList.contains("stp-backdrop") && handleAttemptClose()}>
      <div className="stp-modal" role="dialog" aria-modal="true" aria-label="Raise a support ticket">

        {/* ── Header ── */}
        <div className="stp-header">
          <div className="stp-eyebrow">
            <span className="stp-eyebrow-dot" aria-hidden="true" />
            <span>Agri Drone Simulator Support</span>
          </div>
          <button className="stp-close" onClick={handleAttemptClose} aria-label="Close">✕</button>
        </div>

        {/* ── Confirm Discard Screen ── */}
        {showConfirmDiscard ? (
          <div className="stp-state stp-state--confirm">
            <div className="stp-state-icon">⚠️</div>
            <h2 className="stp-state-title">Discard this ticket?</h2>
            <p className="stp-state-body">
              Your entered details will be lost.
            </p>
            <div className="stp-actions stp-actions--confirm">
              <button className="stp-btn stp-btn--primary" onClick={() => setShowConfirmDiscard(false)}>
                Keep Editing
              </button>
              <button className="stp-btn stp-btn--danger" onClick={handleClose}>
                Discard
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* ── Expired state ── */}
            {phase === "expired" && (
              <div className="stp-state stp-state--expired">
                <div className="stp-state-icon">⏱</div>
                <h2 className="stp-state-title">Help Link Expired</h2>
                <p className="stp-state-body">
                  This help link has expired — please click <strong>Help</strong> in the game again to get a new one.
                </p>
                <button className="stp-btn stp-btn--ghost" onClick={handleClose}>Dismiss</button>
              </div>
            )}

            {/* ── Success state ── */}
            {phase?.type === "success" && (
              <div className="stp-state stp-state--success">
                <div className="stp-state-icon stp-state-icon--green">✓</div>
                <h2 className="stp-state-title">Ticket Raised</h2>
                <p className="stp-state-body">
                  <span className="stp-ticket-num">{phase.ticketNumber}</span> has been created. We'll follow up shortly.
                </p>
                <button className="stp-btn stp-btn--primary" onClick={handleClose}>Done</button>
              </div>
            )}

            {/* ── Form ── */}
            {(phase === "form" || phase === "submitting") && (
              <>
                <div className="stp-title-block">
                  <h2 className="stp-title">Raise a Support Ticket</h2>
                  <p className="stp-subtitle">Describe your issue and we'll look into it.</p>
                </div>

                {/* Stream Username — required, user-fillable */}
                <div className="stp-field">
                  <label className="stp-label">
                    STREAM USER NAME <span className="stp-req" aria-hidden="true">*</span>
                  </label>
                  <input
                    className={`stp-input ${errors.streamUsername ? "stp-input--err" : ""}`}
                    placeholder="Enter your stream username"
                    value={streamUsername}
                    onChange={(e) => { setStreamUsername(e.target.value); setErrors((p) => ({ ...p, streamUsername: undefined })); }}
                  />
                  {errors.streamUsername && <span className="stp-error">{errors.streamUsername}</span>}
                </div>

                {/* Email (required) */}
                <div className="stp-field">
                  <label className="stp-label">
                    Email <span className="stp-req" aria-hidden="true">*</span>
                  </label>
                  <input
                    type="email"
                    className={`stp-input ${errors.email ? "stp-input--err" : ""}`}
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: undefined })); }}
                  />
                  {errors.email && <span className="stp-error">{errors.email}</span>}
                </div>

                {/* Category */}
                <div className="stp-field">
                  <label className="stp-label">
                    Category <span className="stp-req" aria-hidden="true">*</span>
                  </label>
                  <select
                    className={`stp-select ${errors.category ? "stp-input--err" : ""}`}
                    value={category}
                    onChange={(e) => { setCategory(e.target.value); setErrors((p) => ({ ...p, category: undefined })); }}
                  >
                    <option value="">Select a category…</option>
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  {errors.category && <span className="stp-error">{errors.category}</span>}
                </div>

                {/* Description */}
                <div className="stp-field">
                  <label className="stp-label">
                    Description <span className="stp-req" aria-hidden="true">*</span>
                  </label>
                  <textarea
                    className={`stp-textarea ${errors.description ? "stp-input--err" : ""}`}
                    rows={4}
                    placeholder="Describe the issue — what happened, when, any error messages…"
                    value={description}
                    onChange={(e) => { setDescription(e.target.value); setErrors((p) => ({ ...p, description: undefined })); }}
                  />
                  {errors.description && <span className="stp-error">{errors.description}</span>}
                </div>

                {errors.submit && (
                  <p className="stp-form-error">{errors.submit}</p>
                )}

                <div className="stp-actions">
                  <button
                    className="stp-btn stp-btn--primary"
                    onClick={handleSubmit}
                    disabled={phase === "submitting"}
                  >
                    {phase === "submitting" ? (
                      <><span className="stp-spinner" />Submitting…</>
                    ) : (
                      <>Submit Ticket <span aria-hidden="true">→</span></>
                    )}
                  </button>
                  <button className="stp-btn stp-btn--ghost" onClick={handleAttemptClose}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </>
        )}

      </div>
    </div>
  );
}
